import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface SolarSystemOrbitProps {
  width?: number;
  height?: number;
}

export const SolarSystemOrbit: React.FC<SolarSystemOrbitProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height); // Ensure it's square based for the orbits to look right

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    // Clear canvas
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;

    // Draw Sun
    ctx.save();
    const gradient = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, 25);
    gradient.addColorStop(0, '#FFFFFF');
    gradient.addColorStop(0.5, '#FDE047'); // Yellow glow
    gradient.addColorStop(1, 'rgba(253, 224, 71, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 25, 0, Math.PI * 2);
    ctx.fill();

    // Solid Sun core
    ctx.fillStyle = '#FEF08A';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Planets Configuration
    const planets = [
      { name: 'Mercury', color: '#A0A0A0', radius: 3, orbitRadius: 40, speed: 4.0 },
      { name: 'Venus', color: '#E8C46A', radius: 4.5, orbitRadius: 60, speed: 1.6 },
      { name: 'Earth', color: '#4A90D9', radius: 5, orbitRadius: 85, speed: 1.0 },
      { name: 'Mars', color: '#C1440E', radius: 4, orbitRadius: 110, speed: 0.53 },
      { name: 'Jupiter', color: '#C88B3A', radius: 10, orbitRadius: 145, speed: 0.08 },
      { name: 'Saturn', color: '#E4D191', radius: 8, orbitRadius: 185, speed: 0.03, hasRings: true },
      { name: 'Uranus', color: '#B2E0E8', radius: 6.5, orbitRadius: 220, speed: 0.01 },
      { name: 'Neptune', color: '#3F54BA', radius: 6, orbitRadius: 250, speed: 0.006 },
    ];

    // Scale orbits down slightly to fit 400x400 (max radius requested was 330, but half of 400 is 200, so we scale it down slightly or draw out of bounds)
    // The prompt specified exact orbit radiuses (60, 85, 115, 145, 195, 245, 290, 330).
    // If size is 400, max visible radius from center is 200. I will scale everything down by a factor of 0.55 so Neptune (330 * 0.55 = 181) fits inside 200.
    const scaleFactor = (size / 2) / 340; 

    planets.forEach((planet) => {
      const currentOrbitRadius = planet.orbitRadius * scaleFactor;
      
      // Draw orbit path
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.arc(centerX, centerY, currentOrbitRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Calculate planet position
      const time = frameCount * 0.02; // Base speed multiplier
      const angle = time * planet.speed;
      const x = centerX + Math.cos(angle) * currentOrbitRadius;
      const y = centerY + Math.sin(angle) * currentOrbitRadius;

      // Draw Planet
      ctx.beginPath();
      ctx.fillStyle = planet.color;
      ctx.arc(x, y, planet.radius * Math.max(0.6, scaleFactor * 1.2), 0, Math.PI * 2);
      ctx.fill();

      // Draw Saturn's Rings
      if (planet.hasRings) {
        ctx.save();
        ctx.translate(x, y);
        // Tilt the ring
        ctx.rotate(20 * Math.PI / 180);
        
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(228, 209, 145, 0.6)';
        ctx.lineWidth = 1.5;
        // Inner ring
        ctx.ellipse(0, 0, planet.radius * 1.8, planet.radius * 0.4, 0, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(228, 209, 145, 0.3)';
        ctx.lineWidth = 1;
        // Outer ring
        ctx.ellipse(0, 0, planet.radius * 2.2, planet.radius * 0.6, 0, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.restore();
      }
    });

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width, height, backgroundColor: '#0F172A', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '8px', overflow: 'hidden' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};
